import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const FIELDS = ['Name', 'jobReference_c', 'workerReference_c', 'customerRating_c', 'reviewText_c', 'followUpStatus_c', 'escalationReason_c', 'resolvedAt_c'];

export const route = { path: '/reviews', layout: 'owner', access: "profile === 'Agent_c'" };
export const nav = { icon: 'Star', label: 'Review manager', section: 'Performance', profiles: ['Agent_c'], order: 50 };

export default function ReviewManager() {
  const { user } = useAuth();
  const [filter, setFilter] = useState('All');
  const [savingId, setSavingId] = useState('');
  const { data, loading, error, run } = useFetch(() => {
    const { eq } = window.ApperSDK.CoreSDK;
    let query = sdk.table('reviews_c').select(FIELDS);
    if (filter !== 'All') query = query.where(eq('followUpStatus_c', filter));
    return query.orderByDesc('CreatedOn').fetch().then((r) => {
      if (!r.success) throw new Error(r.message || 'Could not load reviews');
      return r.data;
    });
  }, [filter]);

  async function requestFollowUp(review, status) {
    setSavingId(String(review.Id));
    try {
      const result = await sdk.table('audit_logs_c').create({
        Name: 'Review follow-up requested: ' + String(review.Id),
        actorUserId_c: user?.userId || 'unknown',
        actorProfile_c: 'Agent_c',
        action_c: status === 'Escalated' ? 'REVIEW_ESCALATION_REQUESTED' : 'REVIEW_FOLLOW_UP_REQUESTED',
        entityType_c: 'reviews_c',
        entityId_c: String(review.Id),
        afterJson_c: JSON.stringify({ requestedFollowUpStatus: status, currentFollowUpStatus: review.followUpStatus_c || 'New' }),
        source_c: 'Agent portal',
        createdAt_c: new Date().toISOString()
      });
      if (result.messages?.length) toast.error(result.messages[0]);
      if (!result.data?.length) return;
      toast.success('Follow-up request added to the audit trail. The Owner must update the review status.');
      run();
    } catch (error) {
      toast.error(error.message || 'Could not record follow-up request.');
    } finally {
      setSavingId('');
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Customer experience</p>
        <h1 className="mt-2 font-heading text-3xl font-bold">Review manager</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Review customer feedback and submit auditable follow-up or escalation requests without altering the original review.</p>
      </header>
      <section className="glass-panel rounded-[24px] p-5 sm:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3"><label className="grid gap-2 text-sm font-medium">Follow-up status
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3"><option>All</option><option>New</option><option>Contacted</option><option>Escalated</option><option>Resolved</option><option>Closed</option></select>
        </label><Button variant="outline" onClick={run} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh reviews'}</Button></div>
        {error && <p className="mt-4 text-sm text-destructive">Could not load reviews: {error.message}</p>}
        <div className="mt-5 space-y-3">
          {(data ?? []).map((review) => <article key={review.Id} className="rounded-xl border border-border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="font-semibold">{review.Name || 'Customer review'}</p><p className="mt-1 text-xs text-muted-foreground">Job {review.jobReference_c || '—'} · Worker {review.workerReference_c || '—'}</p></div><span className="rounded-full bg-muted px-3 py-1.5 text-xs font-bold">{review.followUpStatus_c || 'New'}</span></div>
            <p className="mt-3 text-sm">{review.customerRating_c ? '★ ' + review.customerRating_c + ' / 5' : 'No rating'}{review.reviewText_c ? ' · ' + review.reviewText_c : ''}</p>
            {review.escalationReason_c && <p className="mt-2 text-sm text-muted-foreground">Escalation note: {review.escalationReason_c}</p>}
            <div className="mt-4 flex flex-wrap gap-2"><Button size="sm" variant="outline" disabled={savingId === String(review.Id)} onClick={() => requestFollowUp(review, 'Contacted')}>Request follow-up</Button><Button size="sm" variant="outline" disabled={savingId === String(review.Id)} onClick={() => requestFollowUp(review, 'Escalated')}>Request escalation</Button></div>
          </article>)}
          {!loading && !(data ?? []).length && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No reviews match this filter. Customer reviews will appear here once supplied by a connected customer feedback workflow.</p>}
        </div>
      </section>
      <p className="text-xs leading-5 text-muted-foreground">Agents cannot delete reviews or directly change Owner-controlled review status. Requests are recorded in the audit trail for review.</p>
      <Button variant="outline" asChild><Link to="/jobs">Open booking queue</Link></Button>
    </div>
  );
}
